// Package imports:
import React, { useMemo } from "react";
import Bugsnag from "@bugsnag/js";
import { Formik } from "formik";
// Component imports:
import Modal from "../../../ui-elements/Modal/Modal";
import { RiskScoreForm } from "./RiskScoreForm";
// Service imports:
import { ErrorMessages } from "../../../services/errorMessages";
// Type imports:
import { IDueDiligenceModalProps, KnowYourCustomer, KnowYourIndividual, RiskScoreDto, RiskScoreFormValues } from "../../../types/DueDiligenceTypes";
import { Fetched } from "../../../types/Types";

interface IProps extends IDueDiligenceModalProps {
    data?: RiskScoreFormValues;
    customers?: Fetched<KnowYourCustomer[]>;
    setCustomers?: React.Dispatch<React.SetStateAction<Fetched<KnowYourCustomer[]>>>;
    individuals?: Fetched<KnowYourIndividual[]>;
    setIndividuals?: React.Dispatch<React.SetStateAction<Fetched<KnowYourIndividual[]>>>;
    riskscore?: RiskScoreDto;
    setRiskscore?: React.Dispatch<React.SetStateAction<RiskScoreDto>>;
}

const RiskScoreModal: React.FC<IProps> = ({
    show,
    close,
    setToastShow,
    setToastType,
    setToastMessage,
    data,
    customers,
    setCustomers,
    individuals,
    setIndividuals,
    riskscore,
    setRiskscore
}) => {
    const [disableSubmit, setDisableSubmit] = React.useState(false);
    const handleSubmit = async (values: RiskScoreFormValues) => {
        try {
            setDisableSubmit(true);
            const response = await fetch('/DueDiligence/SetRiskscore', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            });
            setDisableSubmit(false);

            if (!response.ok) {
                const errorData = await response.text();
                setToastType("error");
                setToastMessage(errorData || 'Ekki tókst að stilla áhættumat');
                setToastShow(true);
                return;
            }

            // Backend returns RiskScoreFormValues plus extra fields (FinishedTimestamp + UpdatedBy info)
            const returned: RiskScoreDto = await response.json();

            const newRiskScoreValue: string | null = returned.riskscore ?? values.riskScore ?? null;
            const newDescription: string = returned.description ?? values.description;

            if (customers && !(customers instanceof Error) && setCustomers) {
                const updatedCustomers = customers.map(c => {
                    if (c.riskscoreId === values.riskscoreId && newRiskScoreValue) {
                        return { ...c, riskscore: newRiskScoreValue, riskscoreDescription: newDescription, status: returned.status };
                    }
                    return c;
                });
                setCustomers(updatedCustomers);
            }

            if (individuals && !(individuals instanceof Error) && setIndividuals) {
                const updatedIndividuals = individuals.map(i => {
                    if (i.riskscoreId === values.riskscoreId && newRiskScoreValue) {
                        return { ...i, riskscore: newRiskScoreValue, riskscoreDescription: newDescription, status: returned.status };
                    }
                    return i;
                });
                setIndividuals(updatedIndividuals);
            }

            // Merge new fields into existing riskscore dto
            if (riskscore && setRiskscore) {
                setRiskscore({
                    ...riskscore,
                    riskscore: newRiskScoreValue ?? riskscore.riskscore,
                    description: newDescription,

                    riskscoreFinishedTimestamp: returned.riskscoreFinishedTimestamp ?? riskscore.riskscoreFinishedTimestamp,
                    riskscoreUpdatedByName: returned.riskscoreUpdatedByName ?? riskscore.riskscoreUpdatedByName,
                    riskscoreUpdatedByUsername: returned.riskscoreUpdatedByUsername ?? riskscore.riskscoreUpdatedByUsername,
                });
            }

            setToastType("success");
            setToastMessage("Áhættumati breytt");
            setToastShow(true);

            close();
        } catch (err) {
            setDisableSubmit(false);
            if (err instanceof Error) Bugsnag.notify(err);
            setToastType("error");
            setToastMessage(ErrorMessages.OtherShort);
            setToastShow(true);
        }
    }

    const subFormComponent = useMemo(() => {
        return (props: { values: RiskScoreFormValues }) => (
            <RiskScoreForm
                values={props.values}
                closeModal={close}
                disableSubmit={disableSubmit}
            />
        )
    }, [disableSubmit]);

    const renderBody = () => (
        <div className="KCL_risk-score-modal">
            <Formik
                initialValues={data ?? {
                    riskscoreId: 0,
                    riskScore: 'low',
                    description: '',
                    name: ''
                }}
                onSubmit={handleSubmit}
                component={subFormComponent}
            />
        </div>
    )

    return (
        <Modal
            show={show}
            onHide={close}
            header={<h3>Breyta áhættumati fyrir {data?.name}</h3>}
            body={renderBody()}
            hasCloseButton
        />
    )
}

export default RiskScoreModal;